import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Check,
  Clock,
  ExternalLink,
  Mail,
  Plus,
  Search,
  Shield,
  Sparkles,
  UserCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { Dropdown } from "../../components/ui/dropdown";
import { PasswordField } from "./password-field";
import { useAuth } from "../../context/AuthContext";
import { getOrganizationMembers, listOrganizationInvitations } from "../settings/settings-api";
import { crmApi } from "./api";
import { avatarColorOf, initialsOf } from "./types";
import type { CrmMember, CrmRole } from "./types";
import { cn } from "../../lib/utils";
import { useToast } from "../../components/ui/toast";

interface AddUserModalProps {
  roles: { id: string; name: string }[];
  currentCrmMembers: CrmMember[];
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

export function AddUserModal({
  roles,
  currentCrmMembers,
  onClose,
  onRefresh,
}: AddUserModalProps) {
  const { account, token, roleId } = useAuth();
  const orgId = account?.organization_id;
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<"org" | "create">("org");
  const [orgSearch, setOrgSearch] = useState("");

  // Role selections for organisation members: userId -> roleId
  const [selectedRoles, setSelectedRoles] = useState<Record<string, string>>({});
  const [assigningId, setAssigningId] = useState<string | null>(null);

  // New user form state
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newRole, setNewRole] = useState(roles[0]?.id || "agent");
  const [newPassword, setNewPassword] = useState(() => `TechLadder@${Math.floor(1000 + Math.random() * 9000)}`);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Fetch all organisation members
  const orgMembersQuery = useQuery({
    queryKey: ["organisation", "members", orgId],
    queryFn: () => getOrganizationMembers(orgId as string, token as string),
    enabled: !!orgId && !!token,
    staleTime: 30_000,
  });

  // Fetch pending invitations if owner/admin
  const invitationsQuery = useQuery({
    queryKey: ["organisation", "invitations", orgId],
    queryFn: () => listOrganizationInvitations(orgId as string, token as string),
    enabled: !!orgId && !!token && (roleId === "owner" || roleId === "admin"),
    staleTime: 30_000,
    retry: false,
  });

  const crmMemberMap = useMemo(() => {
    const map = new Map<string, CrmMember>();
    for (const m of currentCrmMembers) {
      map.set(m.id, m);
      if (m.email) map.set(m.email.toLowerCase(), m);
    }
    return map;
  }, [currentCrmMembers]);

  const orgMembersList = useMemo(() => {
    const raw = (orgMembersQuery.data as any[]) || [];
    return raw.map((item: any) => {
      const uid = item.user_id || item.id;
      const email = item.email || "";
      const name = item.name || email.split("@")[0] || "Teammate";
      const orgRole = item.role_id || (item.role === "owner" ? "owner" : "member");
      const crmUser = crmMemberMap.get(uid) || crmMemberMap.get(email.toLowerCase());
      const hasCrmRole = !!crmUser?.role;
      return {
        id: uid,
        name,
        email,
        phone: item.phone || crmUser?.phone || "",
        orgRole,
        crmUser,
        hasCrmRole,
        crmRole: crmUser?.role || "",
      };
    });
  }, [orgMembersQuery.data, crmMemberMap]);

  const filteredOrgMembers = useMemo(() => {
    if (!orgSearch.trim()) return orgMembersList;
    const q = orgSearch.toLowerCase();
    return orgMembersList.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.phone.toLowerCase().includes(q),
    );
  }, [orgMembersList, orgSearch]);

  const invitationsList = useMemo(() => {
    return (invitationsQuery.data as any[]) || [];
  }, [invitationsQuery.data]);

  const handleAssignRole = async (userId: string, targetRole: string) => {
    setAssigningId(userId);
    try {
      await crmApi.editAgent(userId, { role: targetRole, active: true });
      await onRefresh();
      toast.success(
        "CRM Role Assigned",
        "User is now configured and active in the CRM workspace.",
      );
    } catch (err: any) {
      toast.danger(
        "Assignment Failed",
        err.message || "Failed to assign CRM role.",
      );
    } finally {
      setAssigningId(null);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim() || !newPhone.trim() || !newPassword.trim()) {
      setCreateError("All fields are required.");
      return;
    }

    setCreating(true);
    setCreateError("");

    try {
      await crmApi.createUser({
        name: newName.trim(),
        email: newEmail.trim().toLowerCase(),
        phone: newPhone.trim(),
        password: newPassword.trim(),
        role: newRole,
      });

      await onRefresh();
      toast.success(
        "Teammate Created",
        `${newName} has been added to the Organisation and CRM.`,
      );
      onClose();
    } catch (err: any) {
      setCreateError(err.message || "Failed to create teammate.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      title="Add Users to CRM"
      subtitle="Select existing organisation teammates or invite a new member."
      onClose={onClose}
      width="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Navigation Tabs */}
        <div className="flex border-b border-line dark:border-white/10">
          <button
            type="button"
            onClick={() => setActiveTab("org")}
            className={cn(
              "flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-semibold transition-all",
              activeTab === "org"
                ? "border-brand text-brand font-bold"
                : "border-transparent text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
            )}
          >
            <Building2 className="h-4 w-4" />
            Organisation Members
            <span className="rounded-full bg-soft dark:bg-stone-800 px-2 py-0.5 text-[10px] font-mono">
              {orgMembersList.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("create")}
            className={cn(
              "flex items-center gap-2 border-b-2 px-4 py-2.5 text-xs font-semibold transition-all",
              activeTab === "create"
                ? "border-brand text-brand font-bold"
                : "border-transparent text-ink-3 dark:text-stone-400 hover:text-ink dark:hover:text-white",
            )}
          >
            <UserPlus className="h-4 w-4" />
            Invite / Create New Teammate
          </button>
        </div>

        {/* Tab 1: Organisation Members Roster */}
        {activeTab === "org" && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
              <input
                type="text"
                placeholder="Search organisation teammates by name or email…"
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                className="w-full rounded-xl border border-line dark:border-white/10 bg-soft dark:bg-stone-900/50 py-2 pl-9 pr-3 text-xs text-ink dark:text-white outline-none focus:border-brand focus:ring-1 focus:ring-brand"
              />
            </div>

            {orgMembersQuery.isLoading ? (
              <div className="py-8 text-center text-xs text-ink-3 dark:text-stone-400">
                Loading organisation roster…
              </div>
            ) : filteredOrgMembers.length === 0 ? (
              <div className="rounded-xl border border-line dark:border-white/10 bg-soft/40 p-6 text-center">
                <Users className="mx-auto h-8 w-8 text-ink-3 dark:text-stone-400 opacity-50 mb-2" />
                <p className="text-xs font-semibold text-ink dark:text-white">No organisation members found</p>
                <p className="text-[11px] text-ink-3 dark:text-stone-400 mt-0.5">
                  Try a different search query or invite a new teammate below.
                </p>
              </div>
            ) : (
              <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1">
                {filteredOrgMembers.map((member) => {
                  const currentSelectedRole =
                    selectedRoles[member.id] || member.crmRole || roles[0]?.id || "agent";
                  const isBusy = assigningId === member.id;

                  return (
                    <div
                      key={member.id}
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-xl border p-3 transition-all",
                        member.hasCrmRole
                          ? "border-line dark:border-white/10 bg-elevated dark:bg-stone-900/40"
                          : "border-brand/30 bg-brand-soft/10 dark:bg-brand-soft/5 hover:border-brand/60",
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-2xs",
                            avatarColorOf(member.id),
                          )}
                        >
                          {initialsOf(member.name)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-xs font-bold text-ink dark:text-white">
                              {member.name}
                            </span>
                            <span
                              className={cn(
                                "rounded-md px-1.5 py-0.2 text-[10px] font-semibold tracking-wide uppercase",
                                member.orgRole === "owner"
                                  ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20"
                                  : "bg-stone-200 dark:bg-stone-800 text-ink-2 dark:text-stone-300",
                              )}
                            >
                              {member.orgRole === "owner" ? "Org Owner" : "Org Member"}
                            </span>
                          </div>
                          <p className="truncate text-[11px] text-ink-3 dark:text-stone-400 font-mono">
                            {member.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {member.crmRole === "admin" ? (
                          <span className="rounded-full bg-purple-500/10 border border-purple-500/20 px-2.5 py-1 text-[11px] font-semibold text-purple-700 dark:text-purple-300">
                            Business Admin
                          </span>
                        ) : (
                          <>
                            <div className="w-40">
                              <Dropdown
                                size="sm"
                                ariaLabel={`Role for ${member.name}`}
                                value={currentSelectedRole}
                                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                                onChange={(val) =>
                                  setSelectedRoles((prev) => ({
                                    ...prev,
                                    [member.id]: val,
                                  }))
                                }
                                className="bg-white dark:bg-stone-800"
                              />
                            </div>

                            <button
                              type="button"
                              disabled={isBusy || (member.hasCrmRole && member.crmRole === currentSelectedRole)}
                              onClick={() => handleAssignRole(member.id, currentSelectedRole)}
                              className={cn(
                                "flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-semibold transition-all h-8",
                                member.hasCrmRole
                                  ? member.crmRole !== currentSelectedRole
                                    ? "bg-brand text-white hover:bg-brand-hover shadow-xs"
                                    : "bg-stone-100 dark:bg-stone-800 text-ink-3 dark:text-stone-500 cursor-default"
                                  : "bg-brand text-white hover:bg-brand-hover shadow-xs active:scale-[0.98]",
                              )}
                            >
                              {isBusy ? (
                                "Saving…"
                              ) : member.hasCrmRole ? (
                                member.crmRole !== currentSelectedRole ? "Update" : "Active in CRM"
                              ) : (
                                <>
                                  <Plus className="h-3.5 w-3.5" /> Add to CRM
                                </>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pending Invitations Section */}
            {invitationsList.length > 0 && (
              <div className="rounded-xl border border-line dark:border-white/10 bg-soft/30 p-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-ink-2 dark:text-stone-300 mb-2">
                  <Clock className="h-3.5 w-3.5 text-brand" />
                  <span>Pending Organisation Invitations ({invitationsList.length})</span>
                </div>
                <div className="space-y-1.5 max-h-[120px] overflow-y-auto text-xs">
                  {invitationsList.map((inv: any) => (
                    <div
                      key={inv.id || inv.email}
                      className="flex items-center justify-between rounded-lg bg-white dark:bg-stone-900/60 border border-line dark:border-white/5 px-2.5 py-1.5"
                    >
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-ink-3" />
                        <span className="font-mono text-[11px] text-ink dark:text-stone-200">
                          {inv.email}
                        </span>
                      </div>
                      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-400">
                        {inv.status || "Pending acceptance"}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[10px] text-ink-3 dark:text-stone-400">
                  Invited teammates will automatically appear in the roster above once they accept.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Create Brand-New User Form */}
        {activeTab === "create" && (
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-stone-300">
                  Full Name *
                </label>
                <input
                  required
                  type="text"
                  placeholder="e.g. Sarah Jenkins"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full rounded-lg border border-line dark:border-white/10 bg-transparent px-3 py-2 text-xs text-ink dark:text-white outline-none focus:border-brand"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-stone-300">
                  Phone Number *
                </label>
                <input
                  required
                  type="tel"
                  placeholder="e.g. +91 98765 43210"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  className="w-full rounded-lg border border-line dark:border-white/10 bg-transparent px-3 py-2 text-xs text-ink dark:text-white outline-none focus:border-brand"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-stone-300">
                Work Email *
              </label>
              <input
                required
                type="email"
                placeholder="teammate@company.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full rounded-lg border border-line dark:border-white/10 bg-transparent px-3 py-2 text-xs text-ink dark:text-white outline-none focus:border-brand"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-stone-300">
                  Assigned CRM Role *
                </label>
                <Dropdown
                  size="sm"
                  ariaLabel="Assigned CRM Role"
                  value={newRole}
                  options={roles.filter((r) => r.id !== "").map((r) => ({ value: r.id, label: r.name }))}
                  onChange={setNewRole}
                  className="bg-transparent"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-ink-2 dark:text-stone-300">
                  Initial Password *
                </label>
                <PasswordField
                  required
                  minLength={8}
                  value={newPassword}
                  onChange={setNewPassword}
                  className="w-full rounded-lg border border-line dark:border-white/10 bg-transparent px-3 py-2 text-xs text-ink dark:text-white outline-none focus:border-brand"
                />
              </div>
            </div>

            {createError && (
              <p role="alert" className="text-xs font-semibold text-red-600 dark:text-red-400">
                {createError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-line dark:border-white/10">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-xs font-semibold text-ink-2 dark:text-stone-300 hover:bg-soft"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover disabled:opacity-50"
              >
                {creating ? "Creating…" : "+ Create & Assign Member"}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
